import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Stack,
  alpha,
  useTheme,
  Avatar,
  Badge,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Search,
  Refresh,
  Edit,
  Delete,
  Security,
  Cancel,
  CheckCircle,
  Block,
  Description,
  People,
  Lock,
  Info,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getRoles, createRole, updateRole, deleteRole } from '../redux/actions/userRoleActions';

const RoleMaster = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { roles, loading, error } = useSelector((state) => state.roles || { roles: [], loading: false, error: null });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    await dispatch(getRoles({ search: searchTerm }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    // Filter is done client-side for now
  };

  const handleAddNew = () => {
    setSelectedRole(null);
    setIsEditing(false);
    setFormData({
      name: '',
      description: '',
      is_active: true,
    });
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setIsEditing(true);
    setFormData({
      name: role.name || '',
      description: role.description || '',
      is_active: role.is_active !== false,
    });
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const handleDelete = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (roleToDelete) {
      const result = await dispatch(deleteRole(roleToDelete.id));
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Role deleted successfully',
          severity: 'success'
        });
        loadRoles();
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to delete role',
          severity: 'error'
        });
      }
    }
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Role name is required';
    if (formData.name.trim().length < 2) {
      errors.name = 'Role name must be at least 2 characters';
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const result = isEditing && selectedRole
      ? await dispatch(updateRole(selectedRole.id, formData))
      : await dispatch(createRole(formData));
    
    if (result.success) {
      setSnackbar({
        open: true,
        message: isEditing ? 'Role updated successfully' : 'Role created successfully',
        severity: 'success'
      });
      setFormDialogOpen(false);
      setSelectedRole(null);
      setIsEditing(false);
      loadRoles();
    } else {
      setSnackbar({
        open: true,
        message: result.message || 'Operation failed',
        severity: 'error'
      });
    }
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStats = () => {
    const total = roles.length;
    const active = roles.filter(r => r.is_active !== false).length;
    const inactive = total - active;
    const withPermissions = roles.filter(r => r.permissions && r.permissions.length > 0).length;
    
    return { total, active, inactive, withPermissions };
  };

  const stats = getStats();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Role Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user roles and define access levels for your system
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadRoles} disabled={loading} sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
            }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{ 
              borderRadius: 2,
              px: 3,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Add Role
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            boxShadow: 3,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Total Roles
                  </Typography>
                </Box>
                <Security sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', 
            color: 'white',
            boxShadow: 3,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Active Roles
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
            color: 'white',
            boxShadow: 3,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.inactive}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Inactive Roles
                  </Typography>
                </Box>
                <Block sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
            color: 'white',
            boxShadow: 3,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.withPermissions}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    With Permissions
                  </Typography>
                </Box>
                <Lock sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ boxShadow: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search roles by name or description..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                  '&.Mui-focused': {
                    bgcolor: 'white',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleSearch('')}>
                      <Cancel fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ boxShadow: 1 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Roles Table */}
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', boxShadow: 2 }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
              <CircularProgress size={48} />
            </Box>
          ) : filteredRoles.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 6,
              textAlign: 'center'
            }}>
              <Avatar sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <Security sx={{ fontSize: 48, color: 'primary.main' }} />
              </Avatar>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No roles found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchTerm ? 'Try adjusting your search terms' : 'Create your first role to get started'}
              </Typography>
              {!searchTerm && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddNew}
                  sx={{ borderRadius: 2 }}
                >
                  Add First Role
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                      color: 'primary.main'
                    }
                  }}>
                    <TableCell>Role</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRoles.map((role) => (
                    <TableRow 
                      key={role.id}
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                          cursor: 'pointer'
                        },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar 
                            sx={{ 
                              width: 48, 
                              height: 48, 
                              bgcolor: 'primary.main',
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }}
                          >
                            <Security />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {role.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Role ID: {role.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 400 }}>
                          {role.description ? (
                            <Typography variant="body2" sx={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {role.description}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No description provided
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          badgeContent={role.permissions?.length || 0} 
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.75rem',
                              height: 20,
                              minWidth: 20,
                              padding: '0 6px'
                            }
                          }}
                        >
                          <Chip
                            icon={<Lock fontSize="small" />}
                            label={`${role.permissions?.length || 0} permission${(role.permissions?.length || 0) !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={role.is_active ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
                          label={role.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={role.is_active ? 'success' : 'default'}
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit Role">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEdit(role)}
                              sx={{
                                color: 'primary.main',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.1)
                                }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Role">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDelete(role)}
                              sx={{
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.1)
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Form Dialog */}
      <Dialog 
        open={formDialogOpen} 
        onClose={() => setFormDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 8
          }
        }}
      >
        <form onSubmit={handleFormSubmit}>
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            pb: 2.5,
            pt: 3,
            px: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                {isEditing ? <Edit sx={{ fontSize: 28 }} /> : <Security sx={{ fontSize: 28 }} />}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {isEditing ? 'Edit Role' : 'Create New Role'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {isEditing ? 'Update role information and settings' : 'Define a new role with specific permissions'}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
            <Grid container spacing={3}>
              {/* Role Information Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ 
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Security fontSize="small" />
                  Role Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  error={!!formErrors.name}
                  helperText={formErrors.name || 'Enter a unique name for this role (e.g., Manager, Cashier, Admin)'}
                  required
                  placeholder="e.g., Manager, Cashier, Admin"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Security color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the role's purpose and responsibilities..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <Description color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Provide a clear description of what this role is used for and who should have it
                </Typography>
              </Grid>

              {/* Status Section */}
              <Grid item xs={12}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Info color="primary" sx={{ mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Role Status
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formData.is_active ? 'Active Role' : 'Inactive Role'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formData.is_active 
                                ? 'This role is active and can be assigned to users' 
                                : 'This role is disabled and cannot be assigned to new users'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Info Box */}
              <Grid item xs={12}>
                <Alert 
                  severity="info" 
                  icon={<Info />}
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Next Steps
                  </Typography>
                  <Typography variant="caption" component="div">
                    After creating this role, you can configure its permissions in the{' '}
                    <strong>Role Permissions</strong> page to define what actions users with this role can perform.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
            <Button 
              onClick={() => setFormDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                minWidth: 100
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              startIcon={isEditing ? <Edit /> : <Security />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                minWidth: 120,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              {isEditing ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 8
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <Delete sx={{ fontSize: 28 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Delete Role
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete the role <strong>{roleToDelete?.name}</strong>?
          </Typography>
          {roleToDelete?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {roleToDelete.description}
            </Typography>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Warning: This will permanently delete the role
            </Typography>
            <Typography variant="caption" component="div">
              • All permissions associated with this role will be removed
              <br />
              • Users assigned to this role will lose their role assignment
              <br />
              • This action cannot be undone
            </Typography>
          </Alert>
          {roleToDelete?.permissions && roleToDelete.permissions.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                This role has <strong>{roleToDelete.permissions.length} permission{roleToDelete.permissions.length !== 1 ? 's' : ''}</strong> configured.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            startIcon={<Delete />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 120
            }}
          >
            Delete Role
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleMaster;
